import React, { useCallback, useRef } from 'react';
import {
  RichTextEditor,
  RichTextEditorRef,
} from '@cotera/client/app/components/app/rte';
import {
  AttachmentNode,
  CoteraMention,
  Disclaimer,
  EmojiSuggestionMenu,
  ExpressionNode,
  InputShapeNode,
  JsonTypeBuilderNode,
} from '@cotera/client/app/components/ui/tiptap/extensions';
import { MentionResourcePicker } from '@cotera/client/app/components/resource-picker';
import { useOpenToolModal } from '@cotera/client/v0/components/tool-connections';
import { useAgentMentions } from '../../hooks/use-agent-mentions';
import { useOpenResourceMention } from '../../hooks/use-open-resource-mention';
import { useAgentModel } from './agent.model';
import {
  useAction,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { SaveAgentAction } from './save-agent.action';
import type { Editor as TiptapEditor } from '@tiptap/react';
import { cn } from '@cotera/client/v0/lib/utils';
import { useContinualSave } from '@cotera/client/app/hooks/use-continual-save';
import { useWhoami } from '@cotera/client/app/stores/org';
import { AgentMentionToolsProvider } from './agent-mention-tools-context';
import { AgentEmojiHeaderButton } from './agent-emoji-header-button';
import { AgentEditorTabs } from './agent-editor-tabs';
import { RESOURCE_MEASURE } from '../resources/resource-page-framing';
import { AgentChatsTab } from './agent-chats-tab';
import type { MentionClickData } from '@cotera/client/v0/components/ai-elements/readonly-editor-state';

const makePlaceholder = ({
  editor,
  node,
}: {
  editor: TiptapEditor;
  node: {
    type: { name: string };
    attrs: Record<string, unknown>;
    textContent: string;
  };
}): string => {
  const isH1 = node.type.name === 'heading' && node.attrs['level'] === 1;

  if (isH1 && node.textContent.trim() === '') {
    return 'Give your agent a name...';
  }

  const doc = editor.state.doc;
  let hasBodyText = false;

  doc.descendants((n, _pos) => {
    if (n.type.name === 'heading' && n.attrs['level'] === 1) {
      return true;
    }
    if (n.isText && n.text !== undefined && n.text.trim().length > 0) {
      hasBodyText = true;
      return false;
    }
    return true;
  });

  if (hasBodyText) {
    const funPlaceholders = [
      'Tell me more...',
      'What else should I know?',
      'Keep going...',
      'And then?',
      "What's next?",
      'Continue your thought...',
    ];
    const contentLength = editor.state.doc.textContent.length;
    const index = contentLength % funPlaceholders.length;
    return funPlaceholders[index] ?? 'Tell me more...';
  }

  return "Start writing your Agent's prompt...";
};

/**
 * The single content column for the whole agent editor — the header row, the
 * prose, and the Chats list — so switching tabs never shifts the content
 * sideways.
 *
 * Written as the editor's own gutter rather than a measured copy of it, from
 * `styles.css`: `.notion-like-editor-content` contributes 20px and the
 * ProseMirror surface inside it another `3rem`. The `max-[480px]` step mirrors
 * the media query there that drops the inner padding to 0, leaving just the
 * 20px.
 *
 * The `3rem` has to stay a `rem`. This app sets `html { font-size: 0.8125rem }`
 * (13px), so it resolves to 39px and the column starts at 59px — not the 68px a
 * 16px root would give. Hardcoding the arithmetic put this row 9px right of the
 * prose it is supposed to sit above, which is subtle enough to survive several
 * attempts to fix it by adjusting the button instead.
 *
 * The list is told to add none of its own (`containerClassName="px-0"`), so
 * this is the only inset in play.
 */
const AGENT_CONTENT_INSET =
  'pl-[calc(20px+3rem)] pr-[calc(20px+3rem)] max-[480px]:pl-[20px] max-[480px]:pr-[20px]';

export const AgentStateEditor: React.FC<{
  className?: string;
  editorRef?: React.RefObject<RichTextEditorRef | null>;
  editable?: boolean;
}> = ({ className, editorRef: editorRefProp, editable = true }) => {
  const model = useAgentModel();
  const saveAction = useAction(SaveAgentAction);
  const editorState = useWatchableValue(model.editorStateWatchable);
  const resourceSnapshot = useWatchableValue(model.resourceWatchable);
  const agentUpdatedAtIso = React.useMemo(() => {
    const u = resourceSnapshot?.agent.updatedAt;
    if (u === undefined) {
      return '';
    }
    return u instanceof Date ? u.toISOString() : new Date(u).toISOString();
  }, [resourceSnapshot?.agent.updatedAt]);
  // Inputs are declared in the `input-shape` block, not as inline @input mentions.
  // Existing agents' inline mentions still count (see `WorkspacePromptEditor.inputFields`).
  const {
    options: mentionOptions,
    groups: mentionGroups,
    isLoading: mentionLoading,
    interceptMentionInsert,
  } = useAgentMentions({
    excludeAgentKey: model.agentKey,
  });
  const internalEditorRef = useRef<RichTextEditorRef>(null);
  const editorRef = editorRefProp ?? internalEditorRef;
  const openToolModal = useOpenToolModal();
  const openResourceMention = useOpenResourceMention();
  const whoami = useWhoami((x) => x.whoami);
  const commentAuthor = React.useMemo(
    () => ({ id: whoami.id, name: whoami.name ?? whoami.email }),
    [whoami.id, whoami.name, whoami.email]
  );

  const handleMentionClick = useCallback(
    (data: MentionClickData) => {
      // Artifact-viewable resource mentions open in the side panel — as the page's
      // side panel on the standalone page, or as a sibling artifact tab when this
      // editor is itself embedded as an artifact in a chat (so an @document opens
      // beside the agent instead of navigating away). Falls back to navigation.
      if (openResourceMention(data)) {
        return;
      }
      if (data.t !== 'tool') {
        return;
      }
      const toolKey = data.toolKey ?? data.name;
      if (toolKey !== undefined && toolKey !== '') {
        openToolModal(toolKey);
      }
    },
    [openResourceMention, openToolModal]
  );

  const { scheduleSave } = useContinualSave({
    save: () =>
      void saveAction.execute({ silent: true, agentId: model.agentId }),
    getEditorStateForSave: () => model.getEditorStateForSave(),
    enabled: editable,
  });

  const hasUserFocusedEditorRef = useRef(false);

  const content =
    editorState !== null ? editorState : { type: 'doc' as const, content: [] };

  const contentKey = editorState === null ? 'empty' : 'loaded';

  const handleUpdate = useCallback(
    (editor: TiptapEditor) => {
      // Only treat updates as local overrides after user focus.
      // This keeps server-driven ResourceUpdated changes live when the editor
      // mounts/rehydrates and avoids pinning stale local override state.
      if (hasUserFocusedEditorRef.current) {
        model.editorStateWatchable.set(editor.getJSON());
        model.reconcileHasUnsavedAfterEditorUpdate(editor.getJSON());
        if (editable) {
          scheduleSave(editor);
        }
      }
    },
    [model, scheduleSave, editable]
  );

  const handleEditorFocus = useCallback(() => {
    hasUserFocusedEditorRef.current = true;
  }, []);

  const tab = useWatchableValue(model.editorTabWatchable);

  // Publishes the instance for the comments rail and the header's comment count,
  // which render outside this tree. The Chats tab unmounts the editor, which clears
  // this — and with it the rail, whose threads live in the prose that is no longer
  // on screen.
  const handleEditorReady = useCallback(
    (editor: TiptapEditor | null) => {
      model.editorInstanceWatchable.set(editor);
    },
    [model]
  );

  if (resourceSnapshot === null) {
    return null;
  }

  return (
    <AgentMentionToolsProvider
      agentId={model.agentId}
      agentOrgId={resourceSnapshot.agent.orgId}
      agentUpdatedAt={agentUpdatedAtIso}
    >
      <div
        className={cn(
          'transition-all duration-500 ease-in-out w-full relative shrink-0',
          RESOURCE_MEASURE,
          'shadow-none h-full flex flex-col pt-5',
          className
        )}
      >
        {/* Left-aligned, both of them. The tabs decide what the pane below shows,
            which groups them with the content rather than with the controls that act
            on the agent — the model selector and the header actions, which live over
            on the right. */}
        <div className={cn(AGENT_CONTENT_INSET, 'flex items-center gap-3')}>
          <AgentEmojiHeaderButton
            agentId={model.agentId}
            emoji={resourceSnapshot.agent.emoji}
            editable={editable}
          />
          {!model.isNew ? <AgentEditorTabs /> : null}
        </div>
        {tab === 'editor' ? (
          <div className="flex-1 min-h-0">
            <RichTextEditor
              key={contentKey}
              ref={editorRef as React.RefObject<RichTextEditorRef>}
              className="h-full"
              editable={editable}
              autoFocus={model.isNew}
              titlePlaceholder={
                model.mode === 'artifact'
                  ? 'Working on it....'
                  : 'Give your agent a name...'
              }
              placeholder={makePlaceholder}
              commentAuthor={commentAuthor}
              onEditorReady={handleEditorReady}
              extensions={[
                {
                  extension: Disclaimer,
                  config: {
                    menus: ['turn-into', 'drag-context', 'slash'],
                  },
                },
                {
                  extension: InputShapeNode,
                  config: {
                    menus: ['slash'],
                  },
                },
                {
                  extension: JsonTypeBuilderNode,
                  config: {
                    menus: ['slash'],
                  },
                },
                {
                  extension: ExpressionNode.configure({
                    parser: undefined,
                  }),
                  config: {
                    menus: ['slash'],
                  },
                },
                {
                  extension: AttachmentNode.configure({
                    items: [],
                  }),
                  config: {
                    menus: ['slash'],
                  },
                },
                {
                  extension: CoteraMention.configure({
                    onClick: handleMentionClick,
                  }),
                },
              ]}
              content={content}
              onUpdate={handleUpdate}
              onFocus={handleEditorFocus}
            >
              <MentionResourcePicker
                options={mentionOptions}
                groups={mentionGroups}
                isLoading={mentionLoading}
                interceptMentionInsert={interceptMentionInsert}
                anchorToEditorStart={true}
              />
              <EmojiSuggestionMenu />
            </RichTextEditor>
          </div>
        ) : (
          // No `overflow-hidden` here. The list body bleeds 16px right
          // (`-mr-4 pr-4` in `ResourceListView`) so its scrollbar lands in the
          // gutter instead of inside the content column. Clipping that bleed
          // defeated it — the `pr-4` then pushed the rows left and the scrollbar
          // took the rest, leaving this tab visibly narrower than the prose one.
          // `min-h-0` is what bounds the list; the clip was never load-bearing.
          <div className={cn(AGENT_CONTENT_INSET, 'flex-1 min-h-0')}>
            <AgentChatsTab agentId={model.agentId} />
          </div>
        )}
      </div>
    </AgentMentionToolsProvider>
  );
};
